import { useState, useEffect } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import './machinegroup.css';
import MachineGroupAdd from './machinegroupadd';
import { customerbasedshift, getCustomerUsers } from '../../Services/app/operatorservice';
import MachineGroupEdit from './machinegroupedit';
import { shiftadd } from '../../Services/app/masterservice';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { filterGroupsForUserWithPlants } from '../../Shared/utils/machineGroupAccess';
import Swal from 'sweetalert2';

const MachineGroup = ({IsInGroupReg = false}) => {
  const [machineGroups, setMachineGroups] = useState([]);
  const [plantGroups, setPlantGroups] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogOpenCount, setDialogOpenCount] = useState(0);
  const customerId = localStorage.getItem('CustomerID');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(null);
  const { userRole } = useUserRole();
  const loggedInUserDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
  const userGroupCodes = Array.isArray(loggedInUserDetails?.groups) ? loggedInUserDetails.groups : [];
  const userPlantCodes = Array.isArray(loggedInUserDetails?.plantAccess) ? loggedInUserDetails.plantAccess : [];

  // The list of Cells (Machine Groups) is trimmed to what the logged-in user
  // can access — either directly (their own `groups[]`) or through a Plant
  // they're granted (`plantAccess[]` -> plantgroups[].groupCodes). The raw
  // `machineGroups` state stays unfiltered: it's what Add/Edit dialogs use as
  // their save baseline, and filtering it there would risk writing back a
  // truncated list.
  const visibleMachineGroups = filterGroupsForUserWithPlants(
    machineGroups,
    userRole,
    userGroupCodes,
    plantGroups,
    userPlantCodes
  );

  const handleCloseAddDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setIsAddDialogOpen(false);
    setDialogOpenCount(prevCount => Math.max(0, prevCount - 1));
    fetchMachineGroups();
  };

  const handleCloseEditDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setIsEditDialogOpen(false);
    setEditDialogData(null);
    setDialogOpenCount(prevCount => Math.max(0, prevCount - 1));
    fetchMachineGroups();
  };

  useEffect(() => {
    fetchMachineGroups();
    fetchPlantGroups();
  }, []);

  const fetchPlantGroups = async () => {
    customerbasedshift(customerId, 'plantgroups')
      .then((data) => setPlantGroups(data[0]?.value || []))
      .catch((error) => {
        console.error('Error fetching plant groups:', error);
        setPlantGroups([]);
      });
  };

  const fetchMachineGroups = async () => {
    const key = 'machinegroups';
    customerbasedshift(customerId, key)
      .then(async (data) => {
        const allMachineGroups = data[0]?.value || [];
        setMachineGroups(allMachineGroups);
      })
      .catch(error => {
        console.error("Error fetching machine groups:", error);
        setMachineGroups([]);
      });
  };

  const handleOpenAddDialog = () => {
    console.log('Add Machine Group clicked');
    setIsAddDialogOpen(true);
    setDialogOpenCount(prevCount => prevCount + 1);
  };

  const handleOpenEditDialog = (group) => {
    console.log('Edit Machine Group:', group);
    setEditDialogData(group); 
    setIsEditDialogOpen(true);
    setDialogOpenCount(prevCount => prevCount + 1);
  };

  // A Machine Group is referenced from two places outside its own record:
  //  - Plant Groups (plantgroups[].groupCodes) — a plant that contains this group
  //  - Users (their ThingsBoard additionalInfo.description.groups[]) — anyone
  //    assigned to this group specifically
  // Deleting the group out from under either would silently orphan that
  // reference (a plant card would keep listing a code with no matching group;
  // a user assigned to it would fall back to "sees every group", widening
  // their access without anyone deciding that). So find every blocker first
  // and refuse the delete until they're all reassigned elsewhere.
  const findMachineGroupDependents = async (group) => {
    const [plantsRes, usersRes] = await Promise.all([
      customerbasedshift(customerId, 'plantgroups'),
      getCustomerUsers(customerId),
    ]);

    const allPlants = plantsRes?.[0]?.value || [];
    const blockingPlants = allPlants
      .filter((p) => (p.groupCodes || []).includes(group.code))
      .map((p) => p.name || p.code);

    const allUsers = usersRes?.data || [];
    const blockingUsers = allUsers
      .map((user) => {
        let desc = {};
        try {
          desc = user.additionalInfo?.description ? JSON.parse(user.additionalInfo.description) : {};
        } catch {
          desc = {};
        }
        return { name: user.firstName || desc.userId || 'Unnamed user', groups: Array.isArray(desc.groups) ? desc.groups : [] };
      })
      .filter((u) => u.groups.includes(group.code))
      .map((u) => u.name);

    return { blockingPlants, blockingUsers };
  };

  const handleDeleteMachineGroup = async (row) => {
    let blockingPlants = [];
    let blockingUsers = [];
    try {
      ({ blockingPlants, blockingUsers } = await findMachineGroupDependents(row));
    } catch (error) {
      console.error('Error checking Machine Group dependents:', error);
      Swal.fire('Error', 'Could not verify whether this group is still in use. Please try again.', 'error');
      return;
    }

    if (blockingPlants.length > 0 || blockingUsers.length > 0) {
      const lines = [
        blockingPlants.length > 0 && `<b>Plants:</b> ${blockingPlants.join(', ')}`,
        blockingUsers.length > 0 && `<b>Users:</b> ${blockingUsers.join(', ')}`,
      ].filter(Boolean).join('<br/>');
      Swal.fire({
        icon: 'warning',
        title: 'Cannot delete this Machine Group',
        html: `It is still assigned to the following — reassign them to a different group first:<br/><br/>${lines}`,
      });
      return;
    }

    Swal.fire({
      title: 'Are you sure you want to delete this Machine Group?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const key = 'machinegroups';
          const currentDataResponse = await customerbasedshift(customerId, key);
          const allMachineGroups = currentDataResponse[0]?.value || [];
          const updatedMachineGroups = allMachineGroups.filter((group) => {
            if (
              typeof group.id === 'object' &&
              group.id?.$oid &&
              typeof row.id === 'object' &&
              row.id?.$oid
            ) {
              return group.id.$oid !== row.id.$oid;
            }
            return group.id !== row.id;
          });
          const formData = {
            machinegroups: updatedMachineGroups,
            lastUpdateTs: Date.now(),
          };
          const scope = 'SERVER_SCOPE';
          const response = await shiftadd(formData, customerId, scope);
          if (response?.msg) {
            Swal.fire('Deleted!', response.msg, 'success');
          } else {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Machine Group deleted successfully.",
              timer: 1500,
              showConfirmButton: false,
            });
          }
          fetchMachineGroups();
        } catch (error) {
          console.error('Error deleting Machine Group:', error);
          Swal.fire('Error!', 'Failed to delete Machine Group: ' + error.message, 'error');
        }
      } else {
        Swal.fire('Cancelled', '', 'info');
      }
    });
  };

  return (
    <div className="pages" style={{
      paddingBlockStart: IsInGroupReg ? '0px' : '40px',
    }}>
      <div className="pagecontents">
        <div className="left-labels"  style={{
      padding: IsInGroupReg ? '0 2rem 1rem 0' : '1rem 2rem 1rem 0',
    }}>
          <div className="shift-content-1">
            {IsInGroupReg ? (<h6>Create New</h6>) : (<h5>Machine Group List</h5>)}
            <div className="add_new">
              <Tooltip title="Add Machine Group">
                <IconButton className="circle" onClick={handleOpenAddDialog}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="idle_reason_list">
          {Array.isArray(visibleMachineGroups) && visibleMachineGroups.length > 0 ? (
            visibleMachineGroups
              .slice()
              .sort((a, b) => Number(a.code) - Number(b.code))
              .map((group, idx) => {
                const itemId = group.id || idx;
                return (
                  <div
                    className="idle_reason_item"
                    key={itemId}
                    data-id={group.code}
                  >
                    <div className="icons">
                      <span className="icon-text">{group.code}</span>
                    </div>
                    <h3 className="reason-text">{group.name}</h3>
                    <div className="user_action">
                      <ul>
                        <li>
                          <Tooltip title="Edit Machine Group">
                            <IconButton
                              onClick={() => handleOpenEditDialog(group)}
                            >
                              <EditIcon sx={{ color: 'black' }} />
                            </IconButton>
                          </Tooltip>
                        </li>
                        <li>
                          <Tooltip title="Delete Machine Group">
                            <IconButton
                              onClick={() => handleDeleteMachineGroup(group)}
                            >
                              <DeleteIcon sx={{ color: 'black' }} />
                            </IconButton>
                          </Tooltip>
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              })
          ) : (
            <div style={{ margin: '2rem', color: '#888' }}>
              No machine groups found.
            </div>
          )}
        </div>
      </div>
      <MachineGroupAdd
        open={isAddDialogOpen}
        handleClose={handleCloseAddDialog}
        handleAdd={handleCloseAddDialog}
        dialogOpenCount={dialogOpenCount}
        datasource={machineGroups}
        customerId={customerId}
        setDatasource={setMachineGroups}
      />

      {isEditDialogOpen && editDialogData && (
        <MachineGroupEdit
          open={isEditDialogOpen}
          handleClose={handleCloseEditDialog}
          handleAdd={handleCloseEditDialog}
          dialogData={editDialogData}
          dialogOpenCount={dialogOpenCount}
          datasource={machineGroups}
          customerId={customerId}
          setDatasource={setMachineGroups}
        />
      )}
    </div>
  );
};

export default MachineGroup;
